/**
 * cardData router — dispatches each request to the correct backend.
 *
 * Rule:
 *   language === 'en'  → try digitalcardsClient. If it misses, fall through to tcgdexClient.
 *   language !== 'en'  → tcgdexClient only (foreign not ingested into Supabase yet).
 *
 * `forceSource` on options bypasses this for testing / the /2.0-preview sandbox.
 */
import * as digitalcards from './digitalcardsClient';
import * as tcgdex from './tcgdexClient';
import { DEFAULT_LANGUAGE, SUPPORTED_LANGUAGES } from './types';
import { cached } from './cache';

function backendFor(language, forceSource) {
  if (forceSource === 'digitalcards') return digitalcards;
  if (forceSource === 'tcgdex') return tcgdex;
  return (language || DEFAULT_LANGUAGE) === DEFAULT_LANGUAGE ? digitalcards : tcgdex;
}

/**
 * Try digitalcards first, fall through to TCGdex on null (English only).
 * For non-English or forced, no fallback.
 */
async function primaryWithFallback(fnName, args, { language, forceSource }) {
  const primary = backendFor(language, forceSource);
  const result = await primary[fnName](...args);
  if (result != null && !(Array.isArray(result) && result.length === 0)) return result;
  if (forceSource) return result;
  if ((language || DEFAULT_LANGUAGE) !== DEFAULT_LANGUAGE) return result;
  // Fall through: English request missed in digitalcards → try TCGdex
  if (primary === digitalcards) {
    return tcgdex[fnName](...args);
  }
  return result;
}

/** @param {string} cardId @param {import('./types').LookupOptions} [options] */
export async function getCard(cardId, options = {}) {
  const { language = DEFAULT_LANGUAGE, forceSource, includeVariants = false } = options;
  return primaryWithFallback('getCard', [cardId, { language, includeVariants }], { language, forceSource });
}

/** @param {string} query @param {import('./types').LookupOptions} [options] */
export async function searchCards(query, options = {}) {
  const { language = DEFAULT_LANGUAGE, forceSource, limit } = options;
  return primaryWithFallback('searchCards', [query, { language, limit }], { language, forceSource });
}

/** @param {string} setId @param {import('./types').LookupOptions} [options] */
export async function getSet(setId, options = {}) {
  const { language = DEFAULT_LANGUAGE, forceSource, includeCards = false } = options;
  const fetcher = () => primaryWithFallback('getSet', [setId, { language, includeCards }], { language, forceSource });
  if (forceSource) return fetcher();
  // Only the card-bearing form is worth storing; the bare set row already comes
  // down inside getSets.
  if (!includeCards) return fetcher();
  return cached(`set:${language}:${setId}`, 'setCards', fetcher);
}

/** @param {import('./types').LookupOptions} [options] */
export async function getSets(options = {}) {
  const { language = DEFAULT_LANGUAGE, forceSource, eraId } = options;
  const primary = backendFor(language, forceSource);
  const fetcher = () => primary.getSets({ language, eraId });
  // forceSource is the preview sandbox asking for a specific backend; never
  // serve it something another backend cached.
  if (forceSource) return fetcher();
  return cached(`sets:${language}:${eraId || 'all'}`, 'sets', fetcher);
}

/** @param {string} cardId @param {string|null} variantId @param {import('./types').LookupOptions} [options] */
export async function getCardImage(cardId, variantId = null, options = {}) {
  const { language = DEFAULT_LANGUAGE, forceSource, resolution } = options;
  const primary = backendFor(language, forceSource);
  const url = await primary.getCardImage(cardId, variantId, { language, resolution });
  if (url || forceSource || (language || DEFAULT_LANGUAGE) !== DEFAULT_LANGUAGE) return url;
  return tcgdex.getCardImage(cardId, variantId, { language, resolution });
}

/** @param {string} cardId */
export async function getCardVariants(cardId, options = {}) {
  const { language = DEFAULT_LANGUAGE, forceSource } = options;
  const primary = backendFor(language, forceSource);
  return primary.getCardVariants(cardId);
}

/**
 * Smart catalogue search over our own rows. English only by construction:
 * digitalcards.cards has no language column, so there is nothing to route.
 * A caller that needs another language keeps using searchCards, which does
 * route to TCGdex.
 * @param {object} parsed  the shape from utils/smartSearch.parseQuery
 */
export async function searchCatalogCards(parsed, options = {}) {
  return digitalcards.searchCatalogCards(parsed, options);
}

/**
 * A page of the whole catalogue, sorted and filtered in the database. English
 * only, same reason.
 * `setId` narrows it to one set, for a screen drilling into a set that still
 * wants the catalogue tile's price and set name. `ids` narrows it to a named
 * handful, which is how a chase binder gets its cards sorted and filtered by
 * the same database that sorts and filters Explore.
 * @param {{ from?: number, to?: number, withCount?: boolean, sort?: string, filters?: object, setId?: string|null, ids?: string[]|null }} [options]
 */
export async function listCatalogCards(options = {}) {
  return digitalcards.listCatalogCards(options);
}

/** Market prices for a named handful of catalogue cards, by id. */
export async function getCatalogPrices(cardIds) {
  return digitalcards.getCatalogPrices(cardIds);
}

/** Primary image URLs for a named handful of catalogue cards. */
export async function getCardImages(cardIds) {
  return digitalcards.getCardImages(cardIds);
}

/** How many catalogue cards a filter matches, without fetching them. */
export async function countCatalogCards(options = {}) {
  return digitalcards.countCatalogCards(options);
}

/**
 * Which filter choices a search result actually contains, for fading the rest.
 * @param {{ words?: string[], localIds?: string[], setIds?: string[]|null }} match
 */
export async function searchCatalogFacets(match) {
  return digitalcards.searchCatalogFacets(match);
}

export async function getEras() {
  return cached('eras', 'eras', () => digitalcards.getEras());
}

/** @param {string[]} cardIds */
export async function getCardMeta(cardIds) {
  return digitalcards.getCardMeta(cardIds);
}

export { SUPPORTED_LANGUAGES, DEFAULT_LANGUAGE };
